import { Box, Button, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DropzoneBox from 'components/common/DropzoneBox';
import StepRecipients from 'components/@home/@documents/SentDocuments/steps/StepRecipients/StepRecipients';
import { validateExternalRecipients } from 'components/@home/@documents/SentDocuments/steps/StepRecipients/validateExternalRecipients';
import TableUploadIcon from 'mdi-react/TableArrowUpIcon';
import { array, func, string } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useUpsertEmailContactsMutation } from 'store/app/entities/CompaniesSlice';
import {
  buildExternalRecipientsSampleXlsx,
  EXTERNAL_RECIPIENT_HEADER_SYNONYMS,
  readXlsxFromFile,
} from 'utils/xlsxUtils';
import { I18n } from 'utils/i18n';
import OmmnioRecipientsTable from './OmmnioRecipientsTable';

let externalRecipientRowSeq = 0;
const nextExternalRecipientId = () => {
  externalRecipientRowSeq += 1;
  return `rec-xlsx-${externalRecipientRowSeq}`;
};

const SPREADSHEET_ACCEPT =
  '.xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,application/csv';

const norm = s =>
  String(s || '')
    .toLowerCase()
    .trim();

const matchCol = (headers, synonyms) => {
  const idx = headers.findIndex(h => synonyms.some(s => norm(h).includes(s) || norm(h) === s));
  return idx >= 0 ? idx : -1;
};

const parseSpreadsheet = (rows, headers) => {
  const h = headers.map(norm);
  const emailIdx = matchCol(h, EXTERNAL_RECIPIENT_HEADER_SYNONYMS.email);
  const firstIdx = matchCol(h, EXTERNAL_RECIPIENT_HEADER_SYNONYMS.firstName);
  const lastIdx = matchCol(h, EXTERNAL_RECIPIENT_HEADER_SYNONYMS.lastName);
  const nameIdx = matchCol(h, EXTERNAL_RECIPIENT_HEADER_SYNONYMS.fullName);
  const phoneIdx = matchCol(h, EXTERNAL_RECIPIENT_HEADER_SYNONYMS.phone);
  const departmentIdx = matchCol(h, EXTERNAL_RECIPIENT_HEADER_SYNONYMS.department);
  const jobTitleIdx = matchCol(h, EXTERNAL_RECIPIENT_HEADER_SYNONYMS.jobTitle);
  const codeIdx = matchCol(h, EXTERNAL_RECIPIENT_HEADER_SYNONYMS.code);
  if (emailIdx < 0) return [];
  return rows
    .map(cells => {
      const email = String(cells[emailIdx] || '').trim();
      let name;
      if (nameIdx >= 0) name = String(cells[nameIdx] || '').trim();
      else {
        const fn = firstIdx >= 0 ? String(cells[firstIdx] || '').trim() : '';
        const ln = lastIdx >= 0 ? String(cells[lastIdx] || '').trim() : '';
        name = [fn, ln].filter(Boolean).join(' ');
      }
      const phone = phoneIdx >= 0 ? String(cells[phoneIdx] || '').trim() : '';
      const department = departmentIdx >= 0 ? String(cells[departmentIdx] || '').trim() : '';
      const jobTitle = jobTitleIdx >= 0 ? String(cells[jobTitleIdx] || '').trim() : '';
      const code = codeIdx >= 0 ? String(cells[codeIdx] || '').trim() : '';
      return {
        email,
        name,
        phone,
        department,
        jobTitle,
        code,
        action: 'signer',
      };
    })
    .filter(r => r.email);
};

const StepRecipientsPicker = ({
  recipientTab,
  onRecipientTab,
  selectedEmployeeIds,
  onChangeSelectedEmployeeIds,
  onChangeSelectedEmployeeObjects,
  selectedRecipientsForChips,
  onRemoveRecipientChip,
  emailRecipients,
  setEmailRecipients,
  setNextEnabled,
  setError,
}) => {
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const [spreadsheetPanelOpen, setSpreadsheetPanelOpen] = useState(false);
  const [upsertEmailContacts] = useUpsertEmailContactsMutation();

  const validation = useMemo(() => validateExternalRecipients(emailRecipients), [emailRecipients]);

  const hasOmmnioSelection = selectedEmployeeIds.length > 0;
  const hasEmailRows = emailRecipients.some(r => String(r.email || '').trim());
  const lockEmailCard = hasOmmnioSelection && recipientTab === 'ommnio';
  const lockOmmnioCard = hasEmailRows && recipientTab === 'email';
  const clearOtherFirstMsg = I18n.t('HrBot.RecipientsPicker.clearOtherChannelFirst');

  const handleDownloadSampleXlsx = useCallback(async () => {
    try {
      const buf = await buildExternalRecipientsSampleXlsx();
      const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ommnio-sign-recipients-sample.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.message || String(e));
    }
  }, [setError]);

  useEffect(() => {
    if (recipientTab !== 'email') {
      setSpreadsheetPanelOpen(false);
    }
  }, [recipientTab]);

  useEffect(() => {
    if (recipientTab === 'ommnio') {
      const ok = selectedEmployeeIds.length > 0;
      setNextEnabled(ok);
      setError(ok ? null : I18n.t('HrBot.RecipientsPicker.ommnioPickOne'));
    } else {
      const { ok } = validation;
      setNextEnabled(ok);
      if (!ok && emailRecipients.length) {
        const key = `HrBot.RecipientsStep.${validation.reason}`;
        setError(I18n.t(key));
      } else {
        setError(null);
      }
    }
  }, [
    recipientTab,
    selectedEmployeeIds.length,
    validation,
    emailRecipients.length,
    setError,
    setNextEnabled,
  ]);

  const handleXlsx = useCallback(
    async accepted => {
      const file = accepted?.[0];
      if (!file) return;
      setXlsxBusy(true);
      setError(null);
      try {
        const { XLSX, wb } = await readXlsxFromFile(file);
        const sheetName = wb.SheetNames[0];
        if (!sheetName) {
          setError(I18n.t('HrBot.RecipientsPicker.email.xlsxEmpty'));
          return;
        }
        const sheet = wb.Sheets[sheetName];
        const table = XLSX.utils.sheet_to_json(sheet, { raw: false, header: 1 });
        if (!table?.length) {
          setError(I18n.t('HrBot.RecipientsPicker.email.xlsxEmpty'));
          return;
        }
        const headers = table[0].map(c => String(c ?? ''));
        const body = table.slice(1);
        const parsed = parseSpreadsheet(body, headers);
        // Drop placeholder manual rows (no email) so imports do not keep an empty first line
        // ahead of spreadsheet contacts (OMM-576).
        const existingFilled = emailRecipients.filter(r => String(r.email || '').trim());
        const maxOrder = Math.max(-1, ...existingFilled.map(r => r.order ?? 0));
        const merged = [
          ...existingFilled,
          ...parsed.map((r, i) => ({
            ...r,
            id: nextExternalRecipientId(),
            order: maxOrder + i + 1,
          })),
        ];
        setEmailRecipients(merged);
        setSpreadsheetPanelOpen(false);
        if (parsed.length) {
          upsertEmailContacts({ contacts: parsed }).catch(() => {});
        }
      } catch (e) {
        setError(e?.message || String(e));
      } finally {
        setXlsxBusy(false);
      }
    },
    [emailRecipients, setEmailRecipients, setError, upsertEmailContacts],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Typography variant="h6">{I18n.t('HrBot.RecipientsPicker.title')}</Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        {I18n.t('HrBot.RecipientsPicker.help')}
      </Typography>

      <ToggleButtonGroup
        exclusive
        fullWidth
        value={recipientTab}
        onChange={(e, next) => {
          if (next == null) return;
          if (next === 'ommnio' && lockOmmnioCard) return;
          if (next === 'email' && lockEmailCard) return;
          onRecipientTab(next);
        }}
        sx={theme => ({
          p: 0.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          gap: 0.5,
          '& .MuiToggleButtonGroup-grouped': { border: 0 },
          '& .MuiToggleButton-root': {
            flex: 1,
            border: 'none',
            borderRadius: '8px !important',
            textTransform: 'none',
            py: 1.25,
            px: 1.5,
            fontWeight: 500,
            color: 'text.primary',
            bgcolor: 'transparent',
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.06),
            },
            '&.Mui-selected': {
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main',
              fontWeight: 700,
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.16),
              },
            },
          },
        })}
      >
        <ToggleButton
          value="ommnio"
          disabled={lockOmmnioCard}
          title={lockOmmnioCard ? clearOtherFirstMsg : undefined}
          sx={{ flex: 1, textAlign: 'left' }}
        >
          <Box sx={{ width: '100%', textAlign: 'left' }}>
            <Typography component="span" sx={{ display: 'block', fontWeight: 700 }}>
              {I18n.t('HrBot.RecipientsPicker.channelCards.ommnioTitle')}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                mt: 0.5,
              }}
            >
              {I18n.t('HrBot.RecipientsPicker.channelCards.ommnioSubtitle')}
            </Typography>
          </Box>
        </ToggleButton>
        <ToggleButton
          value="email"
          disabled={lockEmailCard}
          title={lockEmailCard ? clearOtherFirstMsg : undefined}
          sx={{ flex: 1, textAlign: 'left' }}
        >
          <Box sx={{ width: '100%', textAlign: 'left' }}>
            <Typography component="span" sx={{ display: 'block', fontWeight: 700 }}>
              {I18n.t('HrBot.RecipientsPicker.channelCards.emailTitle')}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                mt: 0.5,
              }}
            >
              {I18n.t('HrBot.RecipientsPicker.channelCards.emailSubtitle')}
            </Typography>
          </Box>
        </ToggleButton>
      </ToggleButtonGroup>
      {recipientTab === 'ommnio' && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <OmmnioRecipientsTable
            selectedIds={selectedEmployeeIds}
            onChangeSelectedIds={onChangeSelectedEmployeeIds}
            onChangeSelectedRows={onChangeSelectedEmployeeObjects}
            selectedRecipientsForChips={selectedRecipientsForChips}
            onRemoveRecipientChip={onRemoveRecipientChip}
          />
        </Box>
      )}
      {recipientTab === 'email' && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {!spreadsheetPanelOpen ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                  mb: 1,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {I18n.t('HrBot.RecipientsPicker.email.manualSectionTitle')}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<TableUploadIcon size={18} />}
                  onClick={() => setSpreadsheetPanelOpen(true)}
                >
                  {I18n.t('HrBot.RecipientsPicker.email.uploadSpreadsheetInstead')}
                </Button>
              </Box>
              <StepRecipients
                recipients={emailRecipients}
                setRecipients={setEmailRecipients}
                setNextEnabled={() => {}}
                setError={() => {}}
              />
            </>
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                  mb: 2,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {I18n.t('HrBot.RecipientsPicker.email.uploadSpreadsheetHeading')}
                </Typography>
                <Button variant="text" size="small" onClick={() => setSpreadsheetPanelOpen(false)}>
                  {I18n.t('HrBot.RecipientsPicker.email.enterManuallyInstead')}
                </Button>
              </Box>
              <DropzoneBox
                accept={SPREADSHEET_ACCEPT}
                onDrop={handleXlsx}
                loading={xlsxBusy}
                error={false}
                sx={{ minHeight: 200, mb: 1 }}
                hint={I18n.t('HrBot.RecipientsPicker.email.xlsxDropPlaceholder')}
                hintSecondary={
                  <Typography variant="caption" align="center" component="div">
                    {I18n.t('HrBot.RecipientsPicker.email.xlsxDropHint')}
                  </Typography>
                }
              />
              <Button
                size="small"
                variant="text"
                onClick={handleDownloadSampleXlsx}
                sx={{ alignSelf: 'flex-start' }}
              >
                {I18n.t('HrBot.RecipientsPicker.email.downloadSample')}
              </Button>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

StepRecipientsPicker.propTypes = {
  recipientTab: string.isRequired,
  onRecipientTab: func.isRequired,
  selectedEmployeeIds: array.isRequired,
  onChangeSelectedEmployeeIds: func.isRequired,
  onChangeSelectedEmployeeObjects: func,
  selectedRecipientsForChips: array,
  onRemoveRecipientChip: func,
  emailRecipients: array.isRequired,
  setEmailRecipients: func.isRequired,
  setNextEnabled: func.isRequired,
  setError: func.isRequired,
};

export default StepRecipientsPicker;
